import type { Discovery } from '#~/device/discovery/index.js';
import { useBrands, useTemplates } from '#~/device/registry/hooks.js';
import { deviceMakeAndModel } from '#~/video/components/add/source/brand-sort.js';
import { Sel, STag } from '#~/stag/tag.js';
import { Checkbox } from '@studio/design-system/checkbox';
import { Focusable } from '@studio/design-system/focusable';
import { Ellipses } from '@studio/design-system/label';
import { getVendorContent, vendorName } from '@studio/design-system/themes/vendor.js';
import { Tooltip } from '@studio/design-system/tooltip';
import { castId } from '@studio/models';
import type { Model } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';
import { useAgents } from '../../../../agent/hooks.js';
import { recordsShallowEqual } from '../../../../agent/provider/use-agents-field-eq.js';
import { Layout } from './constants.js';
import { irisRowDisplayName } from './iris-display-name.js';
import { TransportSelect } from './transport-select.jsx';
import type { TableProps, TransportOption } from './types.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
// 3-bucket chip component for per-row chip variants (Multi-Add Phase
// 2 §2.4). Rows whose ids are in `rowChips` render an inline chip
// badge in the Sources cell.
import { Chip } from '#~/room/components/left-tray/chip.jsx';
// "Select model" chip variant for control-only (ONVIF without video
// aspect) discoveries (§2.9). Distinct neutral/blue affordance — NOT
// one of the red/yellow state buckets. Per
// `reference-rules-in-comments` (ai.knowledge/global).
import { SelectModelChip } from './select-model-chip.jsx';
import {
  createEmbeddedDeviceFlowTracker,
  isEmbeddedDiscovery,
  resolveLinkingSurface
} from '#~/analytics/domains/index.js';
import { resolveDeviceAnalyticsCatalogContext } from '#~/analytics/device-context.js';

// The connection string an RTSP(ONVIF) row carries. Mirrors
// Filters[Filter.ONVIF].label in video/components/add/source/constants.ts (the
// source of truth); kept as a local literal so this table's jest module graph
// stays free of that constants module's protocol/Lang import chain.
const RTSP_ONVIF_CONNECTION = 'RTSP (ONVIF)';
// The connection string an NDI row carries (Lang.protocol[Protocol.NDI]); a
// local literal for the same module-graph reason as RTSP_ONVIF_CONNECTION.
const NDI_CONNECTION = 'NDI';

// Column set for the discovery table (aligned with header.tsx HEADER_FIELDS and constants.ts Layout).
type ColumnKey = 'name' | 'ip' | 'connection' | 'brand' | 'bridge_s';
const columns: Array<{
  key: ColumnKey;
  render?: (value: string) => ReactElement;
}> = [
  { key: 'name', render: (value: string) => <Ellipses title={value}>{value}</Ellipses> },
  { key: 'ip', render: (value: string) => <div>{value}</div> },
  { key: 'connection', render: (value: string) => <div>{value}</div> },
  { key: 'brand', render: (value: string) => <div>{value}</div> },
  { key: 'bridge_s', render: (value: string) => <Ellipses title={value}>{value}</Ellipses> }
];

// Add this type to ensure row has all required properties
type RowData = {
  [K in ColumnKey]: string;
} & {
  id: string;
  irisEnabled?: boolean | undefined;
  modelId?: Model['id'] | undefined;
  transports?: TransportOption[] | undefined;
};

/**
 * Direct brand-level name resolver for brand-only fingerprint matches
 * (extended discovery — Change 2B). When the encoder matches a camera to a
 * BRAND but not a specific model it sends `brandId` with NO `modelId`
 * (matcher.go brand fallback), so the `modelId → template.model.brandId` path
 * cannot resolve it. This maps the brand-level `brandId` straight to a name via
 * the `useBrands()` collection so the Brand column populates for these rows
 * too. Returns '' (blank cell, no "Unknown" noise) when the id is missing or
 * the brand is not in the collection.
 *
 * Extracted pure (the `brands` collection is passed in) so the resolver is
 * testable without rendering the full `<Table>` — whose transitive `lodash-es`
 * import makes the component-level `.tsx` suite un-runnable under the current
 * ESM jest setup.
 *
 * Per `tests-require-user-story-comment` + `think-in-systems` (ai.knowledge/global).
 */
export const resolveBrandName = (
  brands: { items?: Record<string, { name?: string } | undefined> | undefined } | undefined,
  brandId: string | undefined
): string => (brandId ? (brands?.items?.[brandId]?.name ?? '') : '');

/** `<Table>` component. */
export const Table = ({
  className,
  discoveries,
  existing,
  selection,
  multiSelectionSet,
  bulkMode,
  disabledIds,
  disabledReason,
  onCheckboxChange,
  // Per-row chip variants. Optional; absent = no chip overlay
  // (preserves single-add P1 behavior per `stay-in-scope`).
  rowChips,
  // Callback fired when a 'select-model' chip is clicked. Optional;
  // when omitted the chip renders passively.
  onSelectModel,
  // Studio ID for analytics attribution (merged from main).
  studioId,
  brandMap = {},
  showBrand = true,
  // Transport switch for a camera row offering several protocols. Optional;
  // absent = the Protocol cell stays a plain label.
  onTransportChange,
  ...props
}: TableProps): ReactElement => {
  const { track } = useAnalytics();

  // Hide the Brand column and use the brand-less grid track when showBrand is false.
  const visibleColumns = showBrand ? columns : columns.filter((c) => c.key !== 'brand');

  // Per ENG-3960 §A.5 (embedded device flow): embedded (Iris-firmware) cameras
  // reuse the shared wizard step events, so the tracker enriches the SAME
  // device_click_discovered emit with is_embedded + linking_surface rather than
  // minting a duplicate event. Stable per flow so its no-double-fire guard holds.
  const embeddedDeviceFlowTracker = useMemo(
    () => createEmbeddedDeviceFlowTracker(track, { linking_surface: resolveLinkingSurface() }),
    [track]
  );

  const handleSelect = useLatestFn((event: MouseEvent<HTMLTableRowElement>) => {
    // In bulk mode, the row name click is a no-op — selection lives in
    // the checkbox to avoid clobbering an in-progress multi-select.
    if (bulkMode === true) return;

    const hit = (event.target as HTMLElement).closest<HTMLDivElement>('div[role="row"]')?.dataset
      .id;

    if (hit) {
      const discoveryId = castId<Discovery['id']>(hit);

      // Find the selected discovery to get its details
      const selectedDiscovery = discoveries.find((d) => d.id === hit);

      // Track the event when a user clicks on a discovered camera.
      // Per ENG-3960 §E12 — brand/model are off-convention; use canonical device_model_id / device_brand_id.
      if (selectedDiscovery) {
        // An "Iris" connection (or an iris-enabled row) is an embedded device.
        // Per rule: investigate-before-adding — shared predicate keeps this step-1
        // classification identical to the wizard steps 2-8 (ENG-3960 §1.7).
        if (isEmbeddedDiscovery(selectedDiscovery)) {
          // Embedded flow: a SINGLE device_click_discovered emit stamped with
          // is_embedded + linking_surface + canonical cohort props (ENG-3960 §E12).
          // Canonical keys only — the off-convention brand/model aliases used on
          // the generic path below are rejected by the track.ts runtime guard.
          embeddedDeviceFlowTracker.trackDiscovered({
            device_id: selectedDiscovery.id,
            deviceType: selectedDiscovery.connection,
            ...(selectedDiscovery.modelId
              ? resolveDeviceAnalyticsCatalogContext(selectedDiscovery.modelId)
              : {}),
            ...(studioId ? { studio_id: studioId } : {})
          });
        } else {
          const modelCfg = selectedDiscovery.modelId
            ? modelConfigs.find((m) => m?.model.id === selectedDiscovery.modelId)
            : null;

          track(EVENT_NAMES.DEVICE_CLICK_DISCOVERED, EVENT_CATEGORIES.DEVICE, {
            deviceId: selectedDiscovery.id,
            deviceType: selectedDiscovery.connection,
            ...(selectedDiscovery.ip && { ip: selectedDiscovery.ip }),
            ...(selectedDiscovery.modelId && {
              device_model_id: String(selectedDiscovery.modelId)
            }),
            ...(modelCfg?.model?.brandId && { device_brand_id: String(modelCfg.model.brandId) }),
            ...(modelCfg?.brand?.name && { brand_name: modelCfg.brand.name }),
            ...(studioId ? { studio_id: studioId } : {})
          });
        }
      }

      selection(selection.value !== discoveryId ? discoveryId : undefined);
    }
  });

  // Wrap `onCheckboxChange` so every bulk-select toggle emits a discovery
  // multi-select event. Toggle direction is derived from the pre-toggle
  // selection set; the payload carries the resulting selected count.
  const trackedCheckboxChange = useLatestFn((discoveryId: Discovery['id']) => {
    const wasSelected = multiSelectionSet?.has(discoveryId) ?? false;
    emitMultiAddAnalytics(EVENT_NAMES.MULTI_ADD_DISCOVERY_SELECTED, {
      selectAction: wasSelected ? 'deselect' : 'select',
      selectedDeviceCount: (multiSelectionSet?.size ?? 0) + (wasSelected ? -1 : 1),
      ...(studioId ? { studioId } : {})
    });
    onCheckboxChange?.(discoveryId);
  });

  // Space / Enter on a focused row in bulk mode toggles the row's
  // checkbox. a11y: matches checkbox role expectations even though the
  // toggle hangs off the row — keyboard parity with checkbox click.
  const handleRowKeyDown = useLatestFn((event: KeyboardEvent<HTMLDivElement>) => {
    if (bulkMode !== true) return;
    if (event.key !== ' ' && event.key !== 'Enter') return;

    const id = event.currentTarget.dataset.id;
    if (!id) return;

    const discoveryId = castId<Discovery['id']>(id);
    if (disabledIds?.has(discoveryId)) return;

    event.preventDefault();
    trackedCheckboxChange(discoveryId);
  });

  // For an agent/bridge to be 'iris enabled', the agent/bridge must be an embedded one.
  const irisEnabledMap = useAgents(
    (µ) => Object.fromEntries(µ.map((a) => [a.id, a.embedded])),
    recordsShallowEqual
  );

  const agentNameMap = useAgents(
    (µ) =>
      Object.fromEntries(µ.map((a) => [a.id, a.embedded ? `${vendorName()} Enabled` : a.name])),
    recordsShallowEqual
  );

  // Get all modelIds up front
  const modelIds = discoveries.reduce<Model['id'][]>((acc, discovery) => {
    if (discovery.modelId) {
      acc.push(discovery.modelId);
    }
    return acc;
  }, []);

  const brands = useBrands();
  const templateConfigs = useTemplates(modelIds);

  const modelConfigs = useMemo(() => {
    return modelIds
      .map((modelId) => {
        const template = templateConfigs[modelId];
        if (!template?.model?.brandId) return null;

        const brand = brands?.items[template.model.brandId];
        return {
          ...template,
          brand
        };
      })
      .filter(Boolean);
  }, [brands, templateConfigs, modelIds]);

  // The Iris Name cell's "{Brand} {Model}" label now comes from the SHARED
  // `deviceMakeAndModel` helper in brand-sort.ts (called at the displayName
  // branch below with this table's `templateConfigs` + `brands`). Extracted so
  // the bulk-add placeholder tile composes the SAME string — byte-identical by
  // construction (spec §1.3 / Bug 1). Per `investigate-before-adding`
  // (ai.knowledge/global): one resolver, no forked label logic.

  // Model resolver for the Sources column; returns '' when the template isn't resolved yet.
  // (rule: reference-rules-in-comments — sort-key == displayed-value).
  const deviceModel = (modelId: Model['id']): string => {
    const config = modelConfigs.find((m) => m!.model.id === modelId);
    return config?.model.name ?? '';
  };

  // Direct brand-level resolver for brand-only fingerprint matches
  // (extended discovery). When the encoder matches a camera to a brand but
  // not a specific model it sends `brandId` with NO `modelId` (matcher.go
  // brand fallback) — so the `modelId → template.model.brandId` path above
  // can't resolve it. Map the brand-level `brandId` straight to a name via
  // `useBrands()` so the Brand column populates for these rows too.
  const brandNameById = (brandId: Discovery['brandId']): string =>
    resolveBrandName(brands, brandId);

  const renderColumnContent = (
    key: ColumnKey,
    row: RowData,
    render?: (value: string) => ReactElement
  ) => {
    if (key === 'name') {
      // Iris: registry make/model when known, advertised mDNS name otherwise (DDA-M1).
      // Fingerprint-matched non-Iris: "{Model} ({IP})" when the Brand column is on.
      const isIris = row.connection === 'Iris';
      const registryLabel = isIris
        ? deviceMakeAndModel(row.modelId, templateConfigs, brands)
        : undefined;
      const hasModel = !!row.modelId;
      // Suppress model-name enrichment for non-Iris rows when showBrand is false.
      const modelName =
        hasModel && showBrand && !isIris ? deviceModel(row.modelId as Model['id']) : '';
      const displayName = isIris
        ? irisRowDisplayName(row.connection, row.name, registryLabel)
        : modelName && row.ip
          ? `${modelName} (${row.ip})`
          : modelName || row.name;
      // Per-row chip overlay. When the discovery's id is in `rowChips`,
      // surface the chip inline next to the name so the user has
      // signal BEFORE re-selecting (e.g. "Failed — Authentication
      // failed" from a prior batch). Default copy comes from the
      // variant; callers (selector.tsx) may override with the
      // discriminated reason string from `failedReasonCopy`.
      const chip = rowChips?.get(row.id as Discovery['id']);
      // Enrichment can hide an NDI row's sender (the NDI source's identity), so
      // the cell keeps it as an STag. Per rule: playwright-stag-selectors-only
      // (ai.knowledge/platform) — e2e matches the sender here, not in the text.
      const senderTag =
        row.connection === NDI_CONNECTION
          ? STag(Sel.AddVideoDeviceDiscovery, 'ndi-sender', row.name)
          : {};
      return (
        <div className='flex items-center overflow-hidden' {...senderTag}>
          {isIris && (
            <img
              alt={`${vendorName()} Enabled Device`}
              className='mr-[6px] h-[18px] w-[18px]'
              src={getVendorContent({
                iris: '/assets/icons/iris_badge_no_bg.png',
                ptzoptics: '/assets/icons/hive_badge.png'
              })}
            />
          )}
          <Ellipses className={isIris ? 'mt-4' : undefined} title={displayName}>
            {displayName}
          </Ellipses>
          {/*
            Select-model variant (§2.9) routes to the distinct
            `<SelectModelChip>` component (neutral/blue + settings
            glyph) instead of the state-bucket `<Chip>` so colorblind
            users can tell the affordance apart from the red/yellow
            state chips. Other variants ('failed', 'needs-attention',
            'added-with-warnings') keep the state-bucket `<Chip>`
            render path unchanged.
          */}
          {chip?.variant === 'select-model' ? (
            <SelectModelChip
              label={chip.label}
              className='ml-8'
              onClick={
                onSelectModel ? (): void => onSelectModel(row.id as Discovery['id']) : undefined
              }
            />
          ) : (
            chip && <Chip variant={chip.variant} label={chip.label} className='ml-8' />
          )}
        </div>
      );
    }

    if (key === 'bridge_s') {
      return <Ellipses title={row.bridge_s}>{row.bridge_s}</Ellipses>;
    }

    // One camera, several protocols: the Protocol cell is the transport choice.
    if (key === 'connection' && onTransportChange && (row.transports?.length ?? 0) > 1) {
      return (
        <TransportSelect
          cameraName={row.name}
          onChange={onTransportChange}
          transports={row.transports ?? []}
          value={row.id as Discovery['id']}
        />
      );
    }

    if (key === 'connection' && row.connection === 'Iris') {
      return <div>{vendorName()}</div>;
    }

    return render?.(row[key]) ?? <div>{row[key]}</div>;
  };

  // Bulk-add feature is wired when the caller passed multiSelectionSet
  // (selector.tsx omits it entirely when `isBulkAddEnabled` is false).
  // Drives the layout choice (6-col with checkbox track vs 5-col legacy)
  // and the per-row checkbox rendering. Per Multi-Add Phase 1 manual QA:
  // without this gate the table reserved an empty 24px gutter for the
  // checkbox track even when the feature was off.
  const bulkAddEnabled = multiSelectionSet !== undefined;
  // Combine the two independent toggles: bulk-add prepends the checkbox
  // track, showBrand drops the brand column.
  const rowLayout = bulkAddEnabled
    ? showBrand
      ? Layout.row
      : Layout.rowWithCheckboxNoBrand
    : showBrand
      ? Layout.rowNoCheckbox
      : Layout.rowNoBrand;

  return (
    <div className={twMerge('select-none px-6 py-4 text-muted [&>*]:h-32', className)} {...props}>
      {discoveries.map((discovery) => {
        // Resolve the brand from the parent's SHARED brandMap (looked up via
        // modelId) so fingerprint-matched RTSP/NDI devices populate the Brand
        // column via the SAME resolver the Brand sort uses — keeping this cell
        // byte-for-byte the Brand sort key for modeled rows. Then (rtsp-assist
        // feature) fall back to a brand-level fingerprint match (`brandId`
        // resolved straight to a name — a brand-only match carries `brandId`
        // but NO `modelId`), and finally the raw wire brand for legacy code
        // paths (ONVIF/NDI) that still push human-readable strings directly.
        const resolvedBrand = discovery.modelId ? (brandMap[String(discovery.modelId)] ?? '') : '';
        // An RTSP(ONVIF) row must never show the maker the ONVIF protocol scan
        // reported (StatusDiscovery.brand, carried on discovery.brand) — only a
        // Device Registry (fingerprint) match may fill its Brand. Drop the wire
        // brand for those rows so they resolve to '' unless resolvedBrand above
        // supplied a registry brand; every other protocol keeps its reported brand.
        // 'RTSP (ONVIF)' is the connection string set by Filters[Filter.ONVIF].label.
        const wireBrand =
          discovery.connection === RTSP_ONVIF_CONNECTION ? undefined : discovery.brand;
        const row: RowData = {
          id: discovery.id,
          name: discovery.name,
          ip: discovery.ip || '',
          connection: discovery.connection,
          // resolved (brandMap) > brand-only fingerprint match (brandId) > wire
          // brand. wireBrand (above) already blanks the ONVIF-protocol brand for
          // RTSP(ONVIF) rows, so those resolve to '' unless a registry or brandId
          // match fills them; every other protocol keeps its reported brand. The
          // Brand sort has no brandId, so the cell==sort-key invariant still holds;
          // the brandId arm only adds display for brand-only matches the sort
          // collates as unresolved. (merge: rtsp-assist brandId arm + the
          // extended-discovery ONVIF wire-brand blanking.)
          brand: resolvedBrand || brandNameById(discovery.brandId) || (wireBrand ?? ''),
          bridge_s: Array.isArray(discovery.bridge_s) ? discovery.bridge_s.join(',') : '',
          irisEnabled: discovery.irisEnabled,
          modelId: discovery.modelId,
          transports: discovery.transports
        };
        // Iris-enabled rows were originally forced into the disabled
        // set — bulk-add did not support Iris cameras in P1; the
        // tooltip directs users back to single-add. Billing-cap callers
        // also push ids into `disabledIds`; the distinct tooltip text
        // comes from `disabledReason`.
        const checkboxId = `discovery-checkbox-${row.id}`;
        const tooltipId = `${checkboxId}-tip`;
        const isChecked = !!multiSelectionSet?.has(row.id as Discovery['id']);
        const isDisabled = !!disabledIds?.has(row.id as Discovery['id']);
        // Reason text for the disabled-checkbox tooltip. Falls back to
        // undefined when no caller-provided reason exists — Iris-enabled
        // rows used to default to "Use single-add for Iris cameras"
        // when bulk-add did not support Iris in P1, but Multi-Add
        // Phase 1 manual QA reopened bulk-add for Iris and the always-
        // prompt + agent-link branch in `runBulkCommit` handles them
        // inline. Surfacing the legacy tooltip on an *enabled* Iris row
        // (now that selector.tsx no longer pushes them into disabledIds)
        // would contradict the checkbox's interactive state — better to
        // leave the tooltip empty and let the caller-provided
        // `disabledReason` map drive the wording exclusively. Per
        // Multi-Add Phase 1 manual QA — bulk-add Iris-enabled support.
        const reason = disabledReason?.get(row.id as Discovery['id']);

        return (
          <Focusable
            className={twMerge(
              'cursor-pointer rounded',
              row.id === selection.value && row.irisEnabled
                ? 'bg-gradient-to-r from-[#6B4D57] via-[#4A5B8C] to-[#4A7B76] font-demibold text-foreground'
                : row.id === selection.value && 'bg-primary font-demibold text-foreground',
              rowLayout
            )}
            data-id={row.id}
            // Discovery arrivals and transport switches must not remount an open picker.
            key={discovery.rowKey ?? row.id}
            onClick={handleSelect}
            onKeyDown={bulkMode === true ? handleRowKeyDown : undefined}
            role='row'
            {...STag(Sel.AddVideoDeviceDiscovery, 'available-devices', row.id)}
            {...props}
          >
            {/* Checkbox cell — first column of the 6-track grid. Rendered
                only when bulk-add is enabled; when the feature is off the
                grid contracts to 5 columns (no leading 24px track) so
                there's no empty gutter. The actual control appears via
                the nested branches below. Tooltip wraps the disabled
                checkbox so screen readers can read the reason via
                aria-describedby. */}
            {bulkAddEnabled && (
              <div
                className='flex items-center justify-center'
                // Stop the row click handler from firing whenever the user
                // interacts with the checkbox area while the feature is ON
                // (multiSelectionSet defined). Bug fix: previously gated on
                // bulkMode === true, but bulkMode only flips after a checkbox
                // is checked — chicken-and-egg. The first click would fall
                // through to the row's handleSelect (single-select), beating
                // the multi-select handler.
                onClick={(e): void => {
                  if (multiSelectionSet !== undefined) e.stopPropagation();
                }}
              >
                {/* Bug fix: gate checkbox VISIBILITY on multiSelectionSet (= feature on)
                  not bulkMode (= user has selection). Otherwise chicken-and-egg:
                  no checkboxes render until user has selection, but user can't
                  select without checkboxes. multiSelectionSet is passed by
                  selector.tsx only when isBulkAddEnabled is true. */}
                {multiSelectionSet !== undefined && reason ? (
                  <Tooltip
                    trigger={
                      <Checkbox.Root
                        id={checkboxId}
                        checked={isChecked}
                        disabled={isDisabled}
                        aria-label={`Select ${row.name}`}
                        aria-describedby={isDisabled ? tooltipId : undefined}
                        onCheckedChange={(): void => {
                          if (isDisabled) return;
                          trackedCheckboxChange(row.id as Discovery['id']);
                        }}
                        className='flex h-16 w-16 items-center justify-center rounded-2 border-1 border-[#A9B3C6] data-[disabled]:cursor-not-allowed data-[state=checked]:bg-primary data-[disabled]:opacity-50'
                      >
                        <Checkbox.Indicator>
                          <i aria-hidden className='i-mdi-tick' />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                    }
                  >
                    <div
                      id={tooltipId}
                      role='tooltip'
                      className='max-w-[240px] rounded-4 bg-black px-12 py-6 text-sm text-white shadow-lg'
                    >
                      {reason}
                    </div>
                  </Tooltip>
                ) : multiSelectionSet !== undefined ? (
                  <Checkbox.Root
                    {...STag(Sel.AddVideoDeviceDiscovery, 'select', row.id)}
                    id={checkboxId}
                    checked={isChecked}
                    disabled={isDisabled}
                    aria-label={`Select ${row.name}`}
                    onCheckedChange={(): void => {
                      if (isDisabled) return;
                      trackedCheckboxChange(row.id as Discovery['id']);
                    }}
                    className='flex h-16 w-16 items-center justify-center rounded-2 border-1 border-[#A9B3C6] data-[disabled]:cursor-not-allowed data-[state=checked]:bg-primary data-[disabled]:opacity-50'
                  >
                    <Checkbox.Indicator>
                      <i aria-hidden className='i-mdi-tick' />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                ) : null}
              </div>
            )}
            {visibleColumns.map(({ key, render }) => (
              <div
                key={key}
                // Exact transport tests must read the protocol independently of the camera name.
                {...(key === 'connection'
                  ? STag(Sel.AddVideoDeviceDiscovery, 'transport_label', row.id)
                  : {})}
              >
                {renderColumnContent(key, row, render)}
              </div>
            ))}
          </Focusable>
        );
      })}

      {existing.map(
        (
          row,
          index // existing is a map of existing devices that have already been added
        ) => (
          <div
            className={twMerge(
              index === 0 && discoveries.length && 'border-t border-base-100',
              'text-primary',
              rowLayout
            )}
            key={row.id}
            role='row'
            {...STag(Sel.AddVideoDeviceDiscovery, 'added-devices', row.id)}
          >
            {/* Empty checkbox slot — keeps existing-rows aligned with
                the 6-column bulk-add grid (already-added devices are
                not selectable). Omitted in legacy single-add layout so
                the 5-column grid contracts without an empty leading
                cell. */}
            {bulkAddEnabled && <div />}
            <div className='flex items-center overflow-hidden'>
              {!!irisEnabledMap[row.agentId] && (
                <img
                  alt='Iris Enabled Device'
                  className='mr-[6px] h-[18px] w-[18px]'
                  src={getVendorContent({
                    iris: '/assets/icons/iris_badge_no_bg.png',
                    ptzoptics: '/assets/icons/hive_badge.png'
                  })}
                />
              )}
              <Ellipses title={row.name}>{row.name}</Ellipses>
            </div>
            <div>
              <Ellipses title={agentNameMap[row.agentId]}>
                {irisEnabledMap[row.agentId] ? (
                  vendorName()
                ) : (
                  <>Connected ({agentNameMap[row.agentId]})</>
                )}
              </Ellipses>
            </div>
          </div>
        )
      )}
    </div>
  );
};
